import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { CLUTCH_URL } from '../../constants';
import { ClutchLogo } from '../ClutchLogo';
import { logError } from '../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTO_MS = 6000;

function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function toDisplay(t) {
  if (t.identity_preference === 'anonymous') return { name: 'Anonymous', role: t.industry || '' };
  if (t.identity_preference === 'partial') {
    return {
      name: t.full_name.trim().split(' ')[0],
      role: [t.job_title, t.industry].filter(Boolean).join(', ') || '',
    };
  }
  return {
    name: t.full_name,
    role: [t.job_title, t.company].filter(Boolean).join(', ') || t.industry || '',
  };
}

const FALLBACK = [];

export const TestimonialsBand = () => {
  const [items, setItems] = useState(FALLBACK);
  const [cur, setCur] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/testimonials/approved`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || data.length === 0) return;
        setItems(data.map((t) => {
          const d = toDisplay(t);
          return { quote: t.experience, name: d.name, role: d.role, photo: t.photo_url || '', rating: t.rating || 5 };
        }));
        setCur(0);
      } catch (err) {
        logError('Failed to load approved testimonials', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const count = items.length;
  const next = useCallback(() => setCur((c) => (c + 1) % count), [count]);
  const prev = useCallback(() => setCur((c) => (c - 1 + count) % count), [count]);

  useEffect(() => {
    if (count <= 1 || paused) return undefined;
    const id = setInterval(() => setCur((c) => (c + 1) % count), AUTO_MS);
    return () => clearInterval(id);
  }, [count, paused]);

  const t = items[cur];

  return (
    <section className="section t-band" id="testimonials">
      <div className="container">
        <div className="heading" style={{ maxWidth: 'none', textAlign: 'center', margin: '0 auto 40px' }}>
          <h2>What our clients say</h2>
        </div>

        <div
          className="t-wrap"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="testimonials-carousel"
        >
          {count === 0 ? (
            <div className="t-card" data-testid="testimonials-empty">
              <span className="t-mark" aria-hidden="true">&ldquo;</span>
              <p className="t-quote">Client stories are on the way. Check back soon, or be the first to share yours.</p>
            </div>
          ) : (
            <>
              {count > 1 && (
                <button className="t-arrow left" onClick={prev} data-testid="testimonial-prev" aria-label="Previous testimonial">
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div className="t-card t-fade" key={cur}>
                <span className="t-mark" aria-hidden="true">&ldquo;</span>
                {t.photo ? (
                  <img
                    className="t-photo"
                    src={t.photo.startsWith('http') ? t.photo : `${API_URL}${t.photo}`}
                    alt={t.name}
                    loading="lazy"
                    data-testid="testimonial-photo"
                  />
                ) : (
                  <div className="t-photo t-avatar" data-testid="testimonial-avatar" aria-hidden="true">{initials(t.name)}</div>
                )}
                <div className="t-stars" data-testid="testimonial-stars" role="img" aria-label={`Rated ${t.rating} out of 5 stars`}>
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={n <= t.rating ? 't-star' : 't-star t-star-empty'} aria-hidden="true" />)}
                </div>
                <p className="t-quote">&ldquo;{t.quote}&rdquo;</p>
                <div className="t-name" data-testid="testimonial-name">{t.name}</div>
                {t.role && <div className="t-role">{t.role}</div>}
              </div>
              {count > 1 && (
                <button className="t-arrow right" onClick={next} data-testid="testimonial-next" aria-label="Next testimonial">
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
              {count > 1 && (
                <div className="t-dots" data-testid="testimonial-dots">
                  {items.map((item, i) => (
                    <button
                      key={`${item.name}-${i}`}
                      className={`t-dot ${i === cur ? 'active' : ''}`}
                      onClick={() => setCur(i)}
                      data-testid={`testimonial-dot-${i}`}
                      aria-label={`Go to testimonial ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="t-cta-row" style={{ marginTop: 34 }}>
          <a className="button" href="/submit-testimonial" data-testid="submit-testimonial-cta">Submit a Testimonial</a>
          <a
            className="t-clutch-link"
            href={CLUTCH_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="clutch-reviews-link"
          >
            Find us on <ClutchLogo height={15} />
          </a>
        </div>
      </div>
    </section>
  );
};
